import React, { useCallback, useState, useEffect } from "react";
import { Creative } from "./Creative/Creative";
import { Box, Link, Tooltip, Typography, useTheme } from "@material-ui/core";
import { useDimensions, usePermissions } from "hooks";
import { useDispatch, useSelector } from "react-redux";
import {
    getMaterialSpecById,
    getMaterialSpecificationDimensions,
    getMaterialSpecUpdatingStatus,
    getOptionalUploadStatusById,
    updateMaterialSpecStatus,
} from "store/features/materialSpecs/specs";
import { OptionalCreative } from "pages/MaterialSpecs/components/CreativesSection";
import { useMaterialSpecs } from "pages/MaterialSpecs/hooks";
import { MaterialDetailsViewModel } from "api/factories/creatives.factory";
import { ApproveButton, ButtonContainer, SecondaryButton } from "components";
import { MaterialSpecActionType, Permission } from "api";
import { useTranslation } from "react-i18next";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import AddBoxOutlinedIcon from "@material-ui/icons/AddBoxOutlined";
import { AddOptionalIconButton } from "./CreativeSection.styled";

interface CreativesSectionProps {
    printed: boolean;
    materialSpecId: number;
    adMaterials: MaterialDetailsViewModel[];
    permissions: Permission[];
    statusCode: number;
}

const CARD_SIZE = 290;

export const CreativesSection = ({
    printed,
    materialSpecId,
    adMaterials,
    statusCode,
    permissions,
}: CreativesSectionProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        selectedMaterialId,
        isOptionalSelected,
        dropOptionalMaterials,
        selectOptional,
        selectedMaterialSpecId,
    } = useMaterialSpecs();
    const { ref, dimensions } = useDimensions<HTMLDivElement>();
    const materialSpecDimensions = useSelector(getMaterialSpecificationDimensions(materialSpecId));
    const isUploadingOptional = useSelector(getOptionalUploadStatusById(materialSpecId));
    const isMaterialSpecStatusUpdating = useSelector(getMaterialSpecUpdatingStatus(materialSpecId));
    const { APPROVE, DELIVER } = usePermissions(permissions);
    const materialSpec = useSelector(getMaterialSpecById(materialSpecId));
    const theme = useTheme();
    const [showOptionalCreativeCard, setShowOptionalCreativeCard] = useState<boolean>(false);

    const handleUpdateMaterialSpecStatus = useCallback(
        (action: MaterialSpecActionType) => {
            dispatch(
                updateMaterialSpecStatus.request({
                    action,
                    status: statusCode,
                    materialSpecId,
                })
            );
        },
        [dispatch, materialSpecId, statusCode]
    );

    useEffect(() => {
        setShowOptionalCreativeCard(false);
    }, [adMaterials.length]);

    if (!adMaterials.length) {
        return null;
    }

    return (
        <>
            <Box
                display="flex"
                flexWrap="wrap"
                pt={2}
                pb={2}
                borderColor="grey.300"
                border={1}
                borderBottom={0}
                borderLeft={0}
                borderRight={0}>
                {adMaterials.map((material) => {
                    return (
                        // ref temporary walk-around => https://github.com/mui-org/material-ui/issues/17010
                        <Box key={material.id} position="relative" p={0.5} width={CARD_SIZE} {...{ ref }}>
                            <Creative
                                printed={printed}
                                expectedSize={materialSpecDimensions}
                                dimensions={dimensions}
                                materialSpecId={materialSpecId}
                                materialDetails={material}
                                selected={!isOptionalSelected && selectedMaterialId === material.id}
                            />
                        </Box>
                    );
                })}
                {!printed && (
                    <Box p={0.5} width={CARD_SIZE} minHeight="220px">
                        {showOptionalCreativeCard ? (
                            <OptionalCreative
                                isUploading={isUploadingOptional}
                                dimensions={dimensions}
                                selected={isOptionalSelected && selectedMaterialSpecId === materialSpecId}
                                onToggleThirdPartyMaterial={() => selectOptional(printed, materialSpecId)}
                                onDrop={dropOptionalMaterials(materialSpecId)}
                                sizeLabel={materialSpecDimensions}
                            />
                        ) : (
                            <AddOptionalIconButton>
                                <AddBoxOutlinedIcon
                                    onClick={() => {
                                        setShowOptionalCreativeCard(true);
                                        selectOptional(printed, materialSpecId);
                                    }}
                                />
                            </AddOptionalIconButton>
                        )}
                    </Box>
                )}
            </Box>
            <Box display="flex" justifyContent="space-between">
                {materialSpec?.adServerUrl && (
                    <Box display="flex" justifyContent="center" alignItems="center" mt={1} ml={0.5}>
                        <Link href={materialSpec.adServerUrl} target="_blank" rel="norefferer" color="secondary">
                            <Box display="flex" alignItems="center">
                                <Typography variant="subtitle2" color="textSecondary">
                                    <Box fontWeight="fontWeightBold">
                                        {t("campaign.creatives.label.openInAdServer")}
                                    </Box>
                                </Typography>
                                <OpenInNewIcon style={{ fontSize: `${theme.spacing(2)}px` }} />
                            </Box>
                        </Link>
                    </Box>
                )}
                <ButtonContainer pt={2} pb={2} display="flex" flex={1} justifyContent="flex-end">
                    {DELIVER?.display && (
                        <Tooltip title={DELIVER.tooltip} interactive>
                            <span>
                                <SecondaryButton
                                    disableRipple
                                    variant="outlined"
                                    color="primary"
                                    disabled={DELIVER.disabled || isMaterialSpecStatusUpdating}
                                    onClick={() => handleUpdateMaterialSpecStatus("DELIVER")}>
                                    {t("buttons.label.deliver")}
                                </SecondaryButton>
                            </span>
                        </Tooltip>
                    )}
                    {APPROVE?.display && (
                        <Tooltip title={APPROVE.tooltip} interactive>
                            <span>
                                <ApproveButton
                                    disableRipple
                                    variant="outlined"
                                    color="primary"
                                    disabled={APPROVE.disabled || isMaterialSpecStatusUpdating}
                                    onClick={() => handleUpdateMaterialSpecStatus("APPROVE")}>
                                    {t("buttons.label.approve")}
                                </ApproveButton>
                            </span>
                        </Tooltip>
                    )}
                </ButtonContainer>
            </Box>
        </>
    );
};
